import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, XCircle, Clock, ArrowLeft, Loader2, Mail, MapPin, Instagram } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

const STATUS_TABS = [
    { key: "pending", label: "Pending", Icon: Clock },
    { key: "approved", label: "Approved", Icon: CheckCircle2 },
    { key: "rejected", label: "Rejected", Icon: XCircle },
];

export default function AdminClaimsPage() {
    const [tab, setTab] = useState("pending");
    const [claims, setClaims] = useState([]);
    const [loading, setLoading] = useState(false);

    const refresh = () => {
        setLoading(true);
        api.get("/claims", { params: { status: tab } })
            .then(({ data }) => setClaims(data || []))
            .catch(() => toast.error("Failed to load claims"))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        refresh();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tab]);

    const decide = async (id, decision) => {
        try {
            await api.post(`/claims/${id}/${decision}`);
            toast.success(`Claim ${decision}d.`);
            refresh();
        } catch {
            toast.error(`Failed to ${decision} claim`);
        }
    };

    return (
        <div className="min-h-screen bg-background">
            <header className="border-b border-border bg-foreground text-background px-6 md:px-12 py-6 flex items-center justify-between">
                <div>
                    <Link
                        to="/"
                        className="font-mono-label text-[10px] opacity-70 hover:opacity-100 flex items-center"
                        data-testid="back-link"
                    >
                        <ArrowLeft className="h-3 w-3 mr-1" /> Back to directory
                    </Link>
                    <h1 className="font-display text-3xl mt-2">
                        Admin · <span className="font-editorial text-primary">Claims queue.</span>
                    </h1>
                </div>
                <span className="font-mono-label text-[10px] opacity-70">Internal · no auth (TODO)</span>
            </header>

            <nav className="border-b border-border flex" data-testid="admin-tabs">
                {STATUS_TABS.map(({ key, label, Icon }) => (
                    <button
                        key={key}
                        onClick={() => setTab(key)}
                        className={`px-6 py-4 font-mono-label text-xs border-r border-border hover-invert flex items-center gap-2 ${
                            tab === key ? "bg-foreground text-background" : "bg-background"
                        }`}
                        data-testid={`tab-${key}`}
                    >
                        <Icon className="h-3.5 w-3.5" /> {label}
                    </button>
                ))}
            </nav>

            <main className="px-6 md:px-12 py-10">
                {loading && (
                    <div className="flex items-center text-muted-foreground py-10">
                        <Loader2 className="animate-spin h-5 w-5 mr-2" /> Loading…
                    </div>
                )}
                {!loading && claims.length === 0 && (
                    <p className="text-muted-foreground py-12 text-center font-mono-label text-xs">
                        No claims with status: {tab}
                    </p>
                )}
                {!loading && claims.length > 0 && (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-px bg-border border border-border">
                        {claims.map((c) => (
                            <article
                                key={c.id}
                                className="bg-background p-6 flex flex-col"
                                data-testid={`claim-${c.id}`}
                            >
                                <div className="flex items-start justify-between mb-3">
                                    <div>
                                        <p className="font-mono-label text-[10px] text-muted-foreground">
                                            {c.women_focus} ·{" "}
                                            {new Date(c.submitted_at).toLocaleString()}
                                            {c.auto_verified && " · AUTO-VERIFIED"}
                                        </p>
                                        <h3 className="font-display text-2xl mt-1">
                                            {c.studio_name}
                                        </h3>
                                    </div>
                                    <div className="flex flex-col items-end gap-1">
                                        <Badge
                                            className={`rounded-none ${
                                                c.status === "approved"
                                                    ? "bg-primary text-primary-foreground"
                                                    : c.status === "rejected"
                                                      ? "bg-destructive text-destructive-foreground"
                                                      : "bg-secondary text-foreground"
                                            }`}
                                        >
                                            {c.status}
                                        </Badge>
                                        {c.auto_verified && (
                                            <Badge className="rounded-none bg-foreground text-background text-[9px]">
                                                domain match
                                            </Badge>
                                        )}
                                    </div>
                                </div>

                                <dl className="space-y-2 text-sm">
                                    <Row icon={MapPin} value={c.address} />
                                    <Row icon={Mail} value={`${c.owner_name} <${c.owner_email}>`} />
                                    <Row icon={Instagram} value={c.instagram} />
                                </dl>

                                {c.classes_offered && (
                                    <p className="text-xs mt-3">
                                        <strong>Classes:</strong> {c.classes_offered}
                                    </p>
                                )}
                                {c.schedule && (
                                    <p className="text-xs mt-1">
                                        <strong>Schedule:</strong> {c.schedule}
                                    </p>
                                )}
                                {c.intro_offer && (
                                    <p className="text-xs mt-1">
                                        <strong>Intro:</strong> {c.intro_offer}
                                    </p>
                                )}
                                {c.notes && (
                                    <p className="text-xs mt-3 text-muted-foreground border-l-2 border-foreground pl-3">
                                        {c.notes}
                                    </p>
                                )}

                                {c.status === "pending" && (
                                    <div className="mt-5 pt-4 border-t border-border flex gap-3">
                                        <Button
                                            onClick={() => decide(c.id, "approve")}
                                            className="rounded-none bg-foreground text-background uppercase tracking-widest hover-invert border border-foreground"
                                            data-testid={`approve-${c.id}`}
                                        >
                                            <CheckCircle2 className="mr-2 h-4 w-4" /> Approve
                                        </Button>
                                        <Button
                                            variant="outline"
                                            onClick={() => decide(c.id, "reject")}
                                            className="rounded-none uppercase tracking-widest hover-invert border-foreground"
                                            data-testid={`reject-${c.id}`}
                                        >
                                            <XCircle className="mr-2 h-4 w-4" /> Reject
                                        </Button>
                                    </div>
                                )}
                            </article>
                        ))}
                    </div>
                )}
            </main>
        </div>
    );
}

function Row({ icon: Icon, value }) {
    if (!value) return null;
    return (
        <div className="flex items-start gap-2 text-xs text-foreground/80">
            <Icon className="h-3.5 w-3.5 mt-0.5 text-primary shrink-0" />
            <span className="break-words">{value}</span>
        </div>
    );
}
